import { useEffect, useState } from "react";
import { formatFullDate } from "@/lib/format";
import type { Customer, StaffMember } from "@/pages/admin/customers/hooks/useCustomers";
import { useFollowUpHistory, followUpActionMeta } from "@/pages/admin/customers/hooks/useFollowUpHistory";

const NOTE_PRESETS = ["已电话联系", "已发送邮件", "等待客户回复", "设备异常待确认"];
const NOTE_MAX = 200;

interface FollowUpModalProps {
  open: boolean;
  customer: Customer | null;
  staff: StaffMember[];
  assigneeWorkload: Record<string, number>;
  busy: boolean;
  onSave: (followUp: boolean, note: string, assignee: string) => void;
  onCancel: () => void;
}

export default function FollowUpModal({ open, customer, staff, assigneeWorkload, busy, onSave, onCancel }: FollowUpModalProps) {
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const { history, loading: historyLoading, error: historyError } = useFollowUpHistory(
    customer?.userId ?? null,
    open,
  );

  useEffect(() => {
    if (open && customer) {
      setNote(customer.followUpNote ?? "");
      setAssignee(customer.followUpAssignee ?? "");
    }
  }, [open, customer]);

  if (!open || !customer) return null;

  const followed = customer.followUp;

  // 该负责人名下「其他」跟进中客户数（排除当前这位，避免重复计数）。
  const selectedLoad = assignee ? assigneeWorkload[assignee] ?? 0 : 0;
  const otherFollowing = assignee
    ? selectedLoad - (customer.followUp && customer.followUpAssignee === assignee ? 1 : 0)
    : 0;
  const busiest = Math.max(0, ...Object.values(assigneeWorkload));
  const isOverloaded = Boolean(assignee) && otherFollowing >= 3 && otherFollowing >= busiest - 1;

  const appendPreset = (preset: string) => {
    setNote((current) => {
      const trimmed = current.trim();
      if (!trimmed) return preset;
      if (trimmed.includes(preset)) return current;
      return `${trimmed}；${preset}`.slice(0, NOTE_MAX);
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="关闭跟进弹窗"
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="lh-fade-up relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-lg border border-background-200 bg-background-50"
      >
        <div className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-flag-2-line text-xl" />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground-950">客服跟进清单</h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              加入清单后该客户会被标记为「跟进中」，并记录一条操作动态，方便客服定向联系与复盘。
            </p>
          </div>
          {followed && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-checkbox-circle-line" />
              </span>
              跟进中
            </span>
          )}
        </div>

        <div className="border-b border-background-200 bg-background-100 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-heading text-sm font-semibold text-foreground-950">{customer.name}</p>
              <p className="mt-0.5 truncate text-xs text-foreground-600">{customer.email || "无邮箱"}</p>
            </div>
            {followed && (
              <span className="flex flex-col items-end gap-1">
                {customer.followedAt && (
                  <span className="text-[11px] text-foreground-500">加入于 {formatFullDate(customer.followedAt)}</span>
                )}
                {customer.followUpAssignee && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
                    <span className="flex h-3 w-3 items-center justify-center">
                      <i className="ri-user-star-line" />
                    </span>
                    {customer.followUpAssignee}
                  </span>
                )}
              </span>
            )}
          </div>
          {customer.riskFlags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {customer.riskFlags.map((flag) => (
                <span
                  key={flag.key}
                  className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800"
                >
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className={flag.key === "inactive" ? "ri-zzz-line" : "ri-swap-box-line"} />
                  </span>
                  {flag.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="border-b border-background-200 px-5 py-4">
          <label htmlFor="follow-up-assignee" className="mb-2 block text-xs font-medium text-foreground-800">
            跟进负责人（选填）
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-primary-700">
              <i className="ri-user-star-line" />
            </span>
            <select
              id="follow-up-assignee"
              value={assignee}
              onChange={(event) => setAssignee(event.target.value)}
              className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-8 pr-8 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="">未分配</option>
              {staff.map((member) => {
                const load = assigneeWorkload[member.name] ?? 0;
                return (
                  <option key={member.id} value={member.name}>
                    {member.name}
                    {load > 0 ? `（${load} 位跟进中）` : ""}
                  </option>
                );
              })}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
          {assignee ? (
            <div
              className={`mt-2 flex flex-wrap items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] ${
                isOverloaded ? "bg-accent-50 text-accent-800" : "bg-primary-50 text-primary-700"
              }`}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className={isOverloaded ? "ri-alert-line" : "ri-user-star-line"} />
              </span>
              <span>
                「{assignee}」名下另有 <strong>{otherFollowing}</strong> 位跟进中客户
                {followed && customer.followUpAssignee === assignee ? "（不含当前客户）" : ""}
              </span>
              {isOverloaded && <span className="font-medium">· 该负责人负荷较高，建议均衡分配</span>}
            </div>
          ) : (
            <p className="mt-1.5 text-[11px] text-foreground-400">
              分配后可在客户管理页按负责人筛选，跟进动作会写入操作动态。
            </p>
          )}
        </div>

        <div className="px-5 py-4">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="follow-up-note" className="text-xs font-medium text-foreground-800">
              跟进备注（选填）
            </label>
            <span className="text-[11px] text-foreground-400">
              {note.length}/{NOTE_MAX}
            </span>
          </div>
          <textarea
            id="follow-up-note"
            value={note}
            maxLength={NOTE_MAX}
            onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
            rows={3}
            placeholder="记录联系情况、客户诉求或后续动作，例如「已电话联系，客户反馈设备更换中」"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {NOTE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => appendPreset(preset)}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                + {preset}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-background-200 px-5 py-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="flex items-center gap-2 text-xs font-medium text-foreground-800">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                <i className="ri-history-line" />
              </span>
              跟进时间轴（{history.length}）
            </p>
            <span className="text-[10px] text-foreground-400">按时间倒序，记录每次备注变更</span>
          </div>
          {historyLoading ? (
            <p className="text-[11px] text-foreground-500">加载中…</p>
          ) : historyError ? (
            <p className="text-[11px] text-accent-700">{historyError}</p>
          ) : history.length === 0 ? (
            <p className="text-[11px] text-foreground-500">暂无跟进记录，加入清单或更新备注后会自动留痕。</p>
          ) : (
            <ol className="max-h-60 overflow-y-auto pr-1">
              {history.map((record, index) => {
                const actionMeta = followUpActionMeta(record.action);
                const dotClass =
                  actionMeta.tone === "primary"
                    ? "bg-primary-100 text-primary-700"
                    : actionMeta.tone === "accent"
                      ? "bg-accent-100 text-accent-700"
                      : "bg-secondary-100 text-secondary-900";
                const chipClass =
                  actionMeta.tone === "primary"
                    ? "bg-primary-100 text-primary-700"
                    : actionMeta.tone === "accent"
                      ? "bg-accent-100 text-accent-800"
                      : "bg-secondary-100 text-secondary-900";
                const isLast = index === history.length - 1;
                return (
                  <li key={record.id} className="relative flex gap-3 pb-3 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${dotClass}`}>
                        <i
                          className={`${
                            record.action === "add"
                              ? "ri-flag-2-line"
                              : record.action === "remove"
                                ? "ri-flag-off-line"
                                : "ri-edit-line"
                          } text-[11px]`}
                        />
                      </span>
                      {!isLast && <span className="mt-1 w-px flex-1 bg-background-200" />}
                    </div>
                    <div className="min-w-0 flex-1 pb-1">
                      <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-foreground-900">
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${chipClass}`}>{actionMeta.label}</span>
                        <span className="text-[10px] text-foreground-400">{formatFullDate(record.created_at)}</span>
                        {record.actor_name && (
                          <span className="text-[10px] text-foreground-500">· {record.actor_name}</span>
                        )}
                      </p>
                      {record.note ? (
                        <p className="mt-1 rounded-md bg-background-100 px-2 py-1 text-[11px] leading-relaxed text-foreground-700">
                          {record.note}
                        </p>
                      ) : (
                        <p className="mt-1 text-[11px] text-foreground-400">（无备注）</p>
                      )}
                      {record.assignee && (
                        <p className="mt-1 flex items-center gap-1 text-[10px] text-primary-700">
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className="ri-user-star-line" />
                          </span>
                          负责人：{record.assignee}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            取消
          </button>
          {followed && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onSave(false, "", "")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-4 py-2 text-sm font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-flag-off-line" />
              </span>
              移出清单
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onSave(true, note.trim(), assignee)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-flag-2-line" />
            </span>
            {busy ? "保存中…" : followed ? "保存备注" : "加入跟进清单"}
          </button>
        </div>
      </div>
    </div>
  );
}